import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { COMP } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export default function Competitors() {
  const { selectedShopId, selectedShop } = useShop();
  const { t } = useLang();
  const qc = useQueryClient();
  const [newDomain, setNewDomain] = useState("");

  const { data: competitors = [] } = useQuery({
    queryKey: ["competitors", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/competitors`)).data,
    enabled: !!selectedShopId,
  });

  const { data: overview } = useQuery({
    queryKey: ["overview", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/overview`)).data,
    enabled: !!selectedShopId,
  });

  const addMutation = useMutation({
    mutationFn: async (domain) =>
      (await api.post(`/shops/${selectedShopId}/competitors`, { domain })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["competitors", selectedShopId] });
      setNewDomain("");
      toast.success(t("add_competitor"));
    },
    onError: () => toast.error("Error adding competitor"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (competitorId) =>
      (await api.delete(`/shops/${selectedShopId}/competitors/${competitorId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["competitors", selectedShopId] }),
  });

  // Merge shop + competitors into chart series
  const chartData = (() => {
    if (!overview) return [];
    const dates = overview.visibility_history.map((p) => p.date);
    return dates.map((date, i) => {
      const row = { date };
      row[selectedShop?.domain || "your_shop"] = overview.visibility_history[i]?.visibility;
      competitors.forEach((c) => {
        row[c.domain] = c.visibility_history[i]?.visibility;
      });
      return row;
    });
  })();

  const seriesKeys = [
    selectedShop?.domain || "your_shop",
    ...competitors.map((c) => c.domain),
  ];

  return (
    <div data-testid={COMP.root} className="space-y-6 rk-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {selectedShop?.domain}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {t("competitors_title")}
          </h1>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Input
            data-testid={COMP.addInput}
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder={t("add_competitor_placeholder")}
            className="h-10 font-mono text-sm min-w-[220px]"
          />
          <Button
            data-testid={COMP.addBtn}
            onClick={() => newDomain && addMutation.mutate(newDomain)}
            disabled={!newDomain || addMutation.isPending}
            className="h-10 gap-2"
          >
            <Plus className="h-4 w-4" />
            {t("add_competitor")}
          </Button>
        </div>
      </header>

      {/* Chart */}
      <div
        data-testid={COMP.chart}
        className="border border-border bg-card rounded-lg p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-medium tracking-tight">
            {t("visibility_trend")}
          </h2>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            index 0–100
          </div>
        </div>
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                tickFormatter={(d) => d.slice(5)}
                interval={4}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                  fontFamily: "IBM Plex Mono",
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                iconSize={8}
              />
              {seriesKeys.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  strokeWidth={i === 0 ? 2.5 : 1.5}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid={COMP.table} className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <Th className="text-left">{t("domain")}</Th>
                <Th className="text-right">{t("visibility")}</Th>
                <Th className="text-right">{t("shared_keywords")}</Th>
                <Th className="text-right">{t("top_10")}</Th>
                <Th className="text-right">{t("trend_30d_short")}</Th>
                <Th className="text-right"></Th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border/70 bg-secondary/40">
                <Td className="text-left">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: CHART_COLORS[0] }}
                    />
                    <span className="font-medium">{selectedShop?.domain}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      {t("your_shop")}
                    </span>
                  </div>
                </Td>
                <Td className="text-right font-mono tabular">{overview?.visibility_score ?? "—"}</Td>
                <Td className="text-right font-mono tabular text-muted-foreground">
                  {overview?.keywords_tracked ?? "—"}
                </Td>
                <Td className="text-right font-mono tabular">{overview?.top_10_count ?? "—"}</Td>
                <Td className="text-right font-mono tabular text-muted-foreground">—</Td>
                <Td></Td>
              </tr>
              {competitors.map((c, i) => (
                <tr
                  key={c.competitor_id}
                  className="border-b border-border/70 last:border-0 hover:bg-secondary/40 transition-colors"
                >
                  <Td className="text-left">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: CHART_COLORS[(i + 1) % CHART_COLORS.length] }}
                      />
                      <span className="font-mono">{c.domain}</span>
                    </div>
                  </Td>
                  <Td className="text-right font-mono tabular">{c.visibility_score}</Td>
                  <Td className="text-right font-mono tabular text-muted-foreground">
                    {c.shared_keywords}
                  </Td>
                  <Td className="text-right font-mono tabular">{c.top_10_count}</Td>
                  <Td className={cn(
                    "text-right font-mono tabular",
                    c.trend_delta_30d > 0
                      ? "text-[hsl(var(--chart-3))]"
                      : c.trend_delta_30d < 0
                        ? "text-destructive"
                        : "text-muted-foreground",
                  )}>
                    {c.trend_delta_30d > 0 ? "▲" : c.trend_delta_30d < 0 ? "▼" : "•"}{" "}
                    {Math.abs(c.trend_delta_30d)}%
                  </Td>
                  <Td className="text-right">
                    <Button
                      data-testid={COMP.deleteBtn(c.competitor_id)}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteMutation.mutate(c.competitor_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Td>
                </tr>
              ))}
              {competitors.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                    —
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className }) {
  return (
    <th
      className={cn(
        "px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-mono font-semibold text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}
function Td({ children, className }) {
  return <td className={cn("px-4 h-12 align-middle", className)}>{children}</td>;
}
